import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.HEP_B,
  displayName: "Hepatitis B",
  serviceType: "administration",
  minimumAgeYears: 0,
  indicationSummary:
    "At increased risk through sexual behaviour (including MSM), sex work, household or sexual contact with an infected person, or travel to areas of moderate or high hepatitis B endemicity.",

  brands: [
    {
      brandId: "engerix-b-paediatric",
      brandName: "Engerix B\u00ae (paediatric \u2014 0\u201315 years)",
      minimumAgeYears: 0,
      maximumAgeYears: 15,
      route: "IM",
      doseDescription: "10 \u00b5g / 0.5 mL",
      administrationNotes:
        "Shake the pre-filled syringe to obtain a slightly opaque white suspension. Administer into the deltoid muscle. In infants and young children, administer into the anterolateral thigh. Do not use the gluteal region.",
    },
    {
      brandId: "engerix-b-adult",
      brandName: "Engerix B\u00ae (adult \u2014 16 years and over)",
      minimumAgeYears: 16,
      route: "IM",
      doseDescription: "20 \u00b5g / 1.0 mL",
      administrationNotes:
        "Shake the pre-filled syringe to obtain a slightly opaque white suspension. Administer into the deltoid muscle. Do not use the gluteal region.",
    },
    {
      brandId: "hbvaxpro-paediatric",
      brandName: "HBvaxPRO\u00ae (paediatric \u2014 0\u201315 years)",
      minimumAgeYears: 0,
      maximumAgeYears: 15,
      route: "IM",
      doseDescription: "5 \u00b5g / 0.5 mL",
      administrationNotes:
        "Shake the vial vigorously to obtain a slightly opaque white suspension. Administer into the deltoid muscle. In infants and young children, administer into the anterolateral thigh.",
    },
    {
      brandId: "hbvaxpro-adult",
      brandName: "HBvaxPRO\u00ae (adult \u2014 16 years and over)",
      minimumAgeYears: 16,
      route: "IM",
      doseDescription: "10 \u00b5g / 1.0 mL",
      administrationNotes:
        "Shake the vial vigorously to obtain a slightly opaque white suspension. Administer into the deltoid muscle.",
    },
    {
      brandId: "heplisav-b",
      brandName: "HEPLISAV B\u00ae \u25bc",
      minimumAgeYears: 18,
      route: "IM",
      doseDescription: "20 \u00b5g / 0.5 mL",
      administrationNotes:
        "HEPLISAV B\u00ae is a solution \u2014 do not shake. Inspect visually for particulate matter or discolouration. Administer into the deltoid muscle. Do not administer subcutaneously. HEPLISAV B\u00ae must NOT be co-administered with any other vaccine at the same visit.",
    },
  ],

  schedules: [
    {
      scheduleId: "hep-b-standard",
      label: "Standard (0, 1, 6 months)",
      offLabel: false,
      doses: [
        { doseNumber: 1, label: "Day 0" },
        {
          doseNumber: 2,
          label: "1 month after dose 1",
          intervalFromDose1Days: 30,
        },
        {
          doseNumber: 3,
          label: "6 months after dose 1",
          intervalFromDose1Days: 180,
        },
      ],
      completionNotes:
        "Resume and complete a delayed course rather than restarting from dose 1.",
    },
    {
      scheduleId: "hep-b-accelerated",
      label: "Accelerated (0, 1, 2 months + 12-month booster)",
      offLabel: false,
      doses: [
        { doseNumber: 1, label: "Day 0" },
        {
          doseNumber: 2,
          label: "1 month after dose 1",
          intervalFromDose1Days: 30,
        },
        {
          doseNumber: 3,
          label: "2 months after dose 1",
          intervalFromDose1Days: 60,
        },
        {
          doseNumber: 4,
          label: "12 months after dose 1 \u2014 booster",
          intervalFromDose1Days: 365,
        },
      ],
      completionNotes:
        "The 12-month booster dose is required for long-term protection and must be arranged before concluding this course.",
    },
    {
      scheduleId: "hep-b-2dose-engerix",
      label:
        "2-dose Engerix B\u00ae (0 and 6 months) \u2014 11\u201315 years only, low risk, compliance assured",
      offLabel: false,
      minimumAgeYears: 11,
      maximumAgeYears: 15,
      doses: [
        { doseNumber: 1, label: "Day 0" },
        {
          doseNumber: 2,
          label: "6 months after dose 1",
          intervalFromDose1Days: 180,
        },
      ],
      completionNotes:
        "This 2-dose schedule uses Engerix B\u00ae 20 \u00b5g (adult dose) in 11\u201315 year-olds. Only suitable for low-risk individuals where adherence to the 6-month return is assured.",
    },
    {
      scheduleId: "hep-b-super-accelerated",
      label:
        "Super-accelerated (0, 7, 21 days + 12-month booster) \u2014 16 years and over",
      offLabel: false,
      minimumAgeYears: 16,
      offLabelRationale:
        "The super-accelerated schedule (days 0, 7, 21) is off-label for patients aged 16\u201317 years. Use in this age group requires documented rationale and patient consent to off-label use.",
      doses: [
        { doseNumber: 1, label: "Day 0" },
        { doseNumber: 2, label: "Day 7", intervalFromDose1Days: 7 },
        { doseNumber: 3, label: "Day 21", intervalFromDose1Days: 21 },
        {
          doseNumber: 4,
          label: "12 months after dose 1 \u2014 booster",
          intervalFromDose1Days: 365,
        },
      ],
      completionNotes:
        "The 12-month booster dose is required for long-term protection and must be arranged before concluding this course. Off-label for ages 16\u201317 \u2014 consent must be documented.",
    },
    {
      scheduleId: "hep-b-heplisav",
      label:
        "HEPLISAV B\u00ae 2-dose schedule (0 and 1 month) \u2014 18 years and over only",
      offLabel: false,
      minimumAgeYears: 18,
      doses: [
        { doseNumber: 1, label: "Day 0" },
        {
          doseNumber: 2,
          label: "1 month after dose 1",
          intervalFromDose1Days: 30,
        },
      ],
      completionNotes:
        "HEPLISAV B\u00ae is not licensed for use as a reinforcing dose in a course started with another hepatitis B vaccine. Do not mix with other hepatitis B vaccine products within the same course.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "hep-b-ex-anaphylaxis",
      question:
        "Has the patient had a confirmed anaphylactic reaction to a previous hepatitis B-containing vaccine or to any of its components (including yeast proteins)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Hepatitis B vaccine is contraindicated. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "hep-b-ex-current-infection",
      question:
        "Does the patient have known markers of current hepatitis B infection (HBsAg positive) or past infection (anti-HBc positive), or is hepatitis B infection currently suspected?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Hepatitis B vaccine is not indicated for patients with current or past hepatitis B infection. Do not supply under this PGD.",
      referralMessage: "Refer to the patient's GP for specialist management.",
    },
    {
      checkId: "hep-b-ex-renal",
      question:
        "Is the patient on a haemodialysis programme, a renal transplant programme, or does the patient have chronic renal failure?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Hepatitis B vaccination in patients on dialysis or with chronic renal failure requires specialist-directed dosing and is outside this PGD.",
      referralMessage:
        "Refer to the patient's renal team or GP for hepatitis B vaccination under a patient-specific direction.",
    },
  ],

  cautions: [
    {
      checkId: "hep-b-caut-bleeding",
      question:
        "Does the patient have a bleeding disorder or is the patient on anticoagulant therapy?",
      cautionMessage:
        "Administer by intramuscular injection with care using a fine-gauge needle. For patients on stable warfarin with INR within therapeutic range, IM injection may proceed with firm pressure applied for at least two minutes afterwards. Note: if HEPLISAV B\u00ae has been selected, it must not be given subcutaneously \u2014 use Engerix B\u00ae or HBvaxPRO\u00ae if the subcutaneous route is required.",
    },
    {
      checkId: "hep-b-caut-myasthenia",
      question: "Does the patient have myasthenia gravis?",
      cautionMessage:
        "Hepatitis B vaccination may worsen or induce myasthenia gravis. Use with caution and document the risk-benefit discussion with the patient.",
    },
    {
      checkId: "hep-b-caut-immunosuppressed",
      question: "Is the patient immunosuppressed due to disease or treatment?",
      cautionMessage:
        "Immunosuppression may reduce the immune response to hepatitis B vaccine. Vaccination should still proceed as the vaccine is safe in immunosuppressed patients. Counsel that protection may be suboptimal. Note: dosing at 40 \u00b5g for people living with HIV is outside this PGD \u2014 refer for a patient-specific direction.",
    },
    {
      checkId: "hep-b-caut-pregnancy",
      question: "Is the patient pregnant or breastfeeding?",
      cautionMessage:
        "Hepatitis B vaccine is inactivated. Vaccination should proceed where the risk of infection is definite and the benefit outweighs any theoretical risk. Advise the patient to inform their GP or midwife.",
    },
    {
      checkId: "hep-b-caut-syncope",
      question:
        "Is there any history of syncope (fainting) following a previous injection or vaccination?",
      cautionMessage:
        "Syncope can occur following vaccination. Ensure the patient is seated or lying down during and for the duration of the observation period after administration.",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  // Conservative: set true for all Hep B because HEPLISAV B is a
  // black triangle product. Yellow Card discussion applies if
  // HEPLISAV B was the selected brand.
  requiresYellowCardDiscussion: true,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine protects against hepatitis B only. It does not protect against hepatitis A, hepatitis C, or hepatitis E.",
    "If you are receiving the accelerated or super-accelerated schedule, a booster dose at 12 months is essential for long-term protection, especially against hepatitis B. Please ensure you return for this dose.",
    "If HEPLISAV B\u00ae was used: this product may cause fatigue, malaise, or dizziness. Do not drive or operate machinery if you feel affected.",
    "Hepatitis B is transmitted through blood-to-blood contact, sexual contact, and from mother to child. Continue to use appropriate precautions until the course is complete.",
    "If you develop symptoms of hepatitis B (jaundice, dark urine, extreme tiredness, or abdominal pain) seek medical advice promptly from your GP or NHS 111.",
    "If HEPLISAV B\u00ae was used: please report any suspected side effects to the MHRA via the Yellow Card scheme at yellowcard.mhra.gov.uk or by calling 0800 731 6789.",
  ],
});
